// =============================================================================
// BREVITY BUDGETS — the word + reading-grade budget POND.
//
// The per-(format-variant, block-kind) word ceiling and Flesch-Kincaid grade
// ceiling the BrevityService scores against. Lifted out of a hardcoded .ts table
// (the old BUDGETS literal) so the budgets are DATA the lake owns, not a literal
// in the algorithm: they vary by format/English variant (au_standard | us_standard
// | gb_standard | …) and get recalibrated over time (a row UPDATE, never a code
// change).
//
// A POND, not just a table: consumers read it through BrevityBudgetSource
// (apps/api/services/lake/BrevityBudgetService.ts), never this table directly — so
// the physical store can relocate (KV, D1, its own Postgres) by swapping the
// adapter, invisible to every consumer.
//
// Conventions mirror au_visa_data.ts / platform_algorithm.ts: uuid id() +
// timestamps (_shared.ts), an index on the lookup key, and a UNIQUE on the natural
// dedup key (format_variant, block_kind) so a re-seed/recalibration upserts in
// place (ON CONFLICT) instead of duplicating.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool, mirrored
// here in Drizzle and exported from index.ts. Nothing dropped or renamed.
// =============================================================================

import { index, integer, pgTable, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, timestamps } from './_shared';

export const brevityBudgets = pgTable(
  'brevity_budgets',
  {
    id: id(),
    /** Format/English variant key — 'au_standard' | 'us_standard' | 'gb_standard'.
     *  au_standard is the default; others are selectable per the role's locale. */
    formatVariant: varchar('format_variant', { length: 24 }).notNull(),
    /** The block kind being budgeted — 'cv_bullet' | 'cv_summary' | … . Mirrors
     *  BrevityService.BlockKind; a coverage test ties the two together. */
    blockKind: varchar('block_kind', { length: 40 }).notNull(),
    /** Hard word ceiling for this block in this variant. */
    words: integer('words').notNull(),
    /** Flesch-Kincaid grade ceiling for this block in this variant. */
    gradeMax: integer('grade_max').notNull(),
    /** Calibration state — 'calibrated' (tuned for this variant) | 'inherited'
     *  (mirrors au_standard pending variant-specific calibration) | 'seed'.
     *  Mirrors the lake staleness banding; surfaced, never silently trusted. */
    verifyState: varchar('verify_state', { length: 16 }).notNull().default('seed'),
    /** Calibration provenance / why this number (e.g. the 2026-05-27 grade note). */
    notes: varchar('notes', { length: 600 }),
    ...timestamps,
  },
  (t) => ({
    variantIdx: index('brevity_budgets_variant_idx').on(t.formatVariant),
    // UNIQUE (format_variant, block_kind) — one budget per block per variant, so a
    // re-seed/recalibration upserts in place. Apply via the Neon tool:
    //   CREATE UNIQUE INDEX IF NOT EXISTS brevity_budgets_key_uq
    //     ON brevity_budgets (format_variant, block_kind);
    keyUq: uniqueIndex('brevity_budgets_key_uq').on(t.formatVariant, t.blockKind),
  }),
);
